import React, { useState, Suspense, lazy } from "react";
import { GOOGLE_REVIEWS, HOME_REVIEWS } from "../data/googleReviews";

// Below-the-fold sections are code-split so the first paint only needs Header + Hero + reviews strip.
const NearMeSection = lazy(() => import("../components/NearMeSection"));
const AuthorizedBrands = lazy(() => import("../components/AuthorizedBrands"));
const Brands = lazy(() => import("../components/Brands"));
const Services = lazy(() => import("../components/Services"));
const ChecklistBanner = lazy(() => import("../components/ChecklistBanner"));
const Commercial = lazy(() => import("../components/Commercial"));
const SeasonalBanner = lazy(() => import("../components/SeasonalBanner"));
const PromoVideo = lazy(() => import("../components/PromoVideo"));
const RatedBanner = lazy(() => import("../components/RatedBanner"));
const GoogleTestimonials = lazy(() => import("../components/GoogleTestimonials"));
const CommonProblems = lazy(() => import("../components/CommonProblems"));
const ServiceArea = lazy(() => import("../components/ServiceArea"));
const Features = lazy(() => import("../components/Features"));
const Testimonials = lazy(() => import("../components/Testimonials"));
const Team = lazy(() => import("../components/Team"));
const EasyInvoicing = lazy(() => import("../components/EasyInvoicing"));
const BlogSection = lazy(() => import("../components/BlogSection"));
const PaymentMethods = lazy(() => import("../components/PaymentMethods"));
const FAQ = lazy(() => import("../components/FAQ"));
const Contact = lazy(() => import("../components/Contact"));
const CTABanner = lazy(() => import("../components/CTABanner"));
const BookingModal = lazy(() => import("../components/BookingModal"));
const Toaster = lazy(() => import("../components/ui/toaster").then((m) => ({ default: m.Toaster })));
import Header from "../components/Header";
import Deferred from "../components/Deferred";
import Hero from "../components/Hero";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import { faqSchema, serviceSchema } from "../lib/seo";
import { FAQS } from "../mock";

const HomePage = () => {
  const [bookingOpen, setBookingOpen] = useState(false);
  const openBooking = () => setBookingOpen(true);

  // NOTE: LocalBusiness JSON-LD is served statically from public/index.html so
  // non-JS crawlers see it too. Only inject the FAQ schema dynamically here.
  const schemas = [
    serviceSchema({
      title: "Same-Day Home Appliance Repair in Decatur & Metro Atlanta",
      metaDescription:
        "Mobile residential refrigerator, washer, dryer, oven, dishwasher and ice maker repair within 25 miles of Decatur and DeKalb County, GA. $89 diagnostic fee, credited toward your repair.",
      serviceType: "Appliance Repair",
      startingAt: 89,
    }),
    faqSchema(FAQS),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Appliance Repair Decatur GA"
        description="Appliance repair Decatur GA: same-day mobile appliance repair across DeKalb County. Fridges, washers, dryers, ovens & dishwashers. Call (678) 525-0474!"
        path="/"
        schemas={schemas}
      />
      <Header onBookClick={openBooking} />
      <main>
        <Hero onBookClick={openBooking} />
        <Suspense fallback={null}>
          <PromoVideo page="home" />
        </Suspense>
        <Suspense fallback={<div style={{ minHeight: 420 }} aria-hidden="true" />}>
          <RatedBanner />
          <GoogleTestimonials reviews={HOME_REVIEWS.map((k) => GOOGLE_REVIEWS[k])} background="bg-slate-50" testIdPrefix="home-google-testimonials" />
        </Suspense>
        <Deferred minHeight={600}>
          <Suspense fallback={null}>
            <NearMeSection />
            <section className="py-10 bg-white">
              <div className="max-w-7xl mx-auto px-6">
                <PaymentMethods />
              </div>
            </section>
            <AuthorizedBrands />
            <Brands />
            <Services onBookClick={openBooking} />
          </Suspense>
        </Deferred>
        <Deferred minHeight={600}>
          <Suspense fallback={null}>
            <Commercial />
            <ChecklistBanner />
            <SeasonalBanner />
            <EasyInvoicing />
            <CommonProblems onBookClick={openBooking} />
            <CTABanner onBookClick={openBooking} />
          </Suspense>
        </Deferred>
        <Deferred minHeight={600}>
          <Suspense fallback={null}>
            <ServiceArea />
            <Features />
            <Testimonials />
            <Team />
            <BlogSection />
            <FAQ />
            <Contact onBookClick={openBooking} />
          </Suspense>
        </Deferred>
      </main>
      <Deferred minHeight={520}>
        <Footer />
      </Deferred>
      <Suspense fallback={null}>
        {bookingOpen && (
          <BookingModal open={bookingOpen} onOpenChange={setBookingOpen} />
        )}
      </Suspense>
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
    </div>
  );
};

export default HomePage;
